import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ContentEditable from 'react-contenteditable';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

// Enhanced component icons mapping with better categorization
const componentIcons = {
  // Header Components
  logo: '🖼️',
  header: '📋',
  companyInfo: '🏢',
  
  // Address Components
  address: '📍',
  billShip: '🏷️',
  contactInfo: '📞',
  
  // Invoice Details
  invoiceDetails: '📄',
  keyValueList: '🧾',
  summaryBox: '📊',
  
  // Content Components
  lineItems: '📋',
  totals: '➕',
  paymentInstructions: '🏦',
  textBlock: '💬',
  divider: '➖',
  
  // Footer Components
  footerColumns: '📑',
  termsConditions: '📜',
  signature: '✍️',
  
  // Advanced Components
  chart: '📈',
  qrCode: '📱',
  barcode: '📊',
  customHtml: '🔧'
};

// Enhanced Component Library with better defaults and validation
const componentLibrary = {
  logo: {
    type: 'logo',
    src: 'https://via.placeholder.com/250x100.png?text=Your+Logo',
    alt: 'Company Logo',
    styles: { 
      textAlign: 'left', 
      width: '250px',
      maxWidth: '100%',
      height: 'auto'
    },
    validation: {
      required: false,
      maxSize: '2MB',
      allowedTypes: ['image/png', 'image/jpeg', 'image/svg+xml']
    }
  },
  header: {
    type: 'header',
    content: '<h1>RECHNUNG</h1>',
    styles: { 
      textAlign: 'right', 
      color: '#000000', 
      fontSize: '36px',
      fontWeight: 'bold',
      margin: '0 0 20px 0'
    }
  },
  companyInfo: {
    type: 'companyInfo',
    content: `
      <div>
        <h3>{{company_name}}</h3>
        <p>{{company_address}}</p>
        <p>Tel: {{company_phone}} | Email: {{company_email}}</p>
        <p>VAT: {{company_vat}}</p>
      </div>
    `,
    styles: { 
      textAlign: 'left', 
      color: '#333333', 
      fontSize: '14px',
      lineHeight: '1.5'
    }
  },
  address: {
    type: 'address',
    content: `
      <div>
        <strong>Bill To:</strong><br>
        {{bill_to_name}}<br>
        {{bill_to_address}}<br>
        VAT: {{bill_to_vat}}
      </div>
    `,
    styles: { 
      textAlign: 'left', 
      color: '#333333', 
      fontSize: '14px',
      lineHeight: '1.6'
    }
  },
  invoiceDetails: {
    type: 'invoiceDetails',
    content: `
      <div style="text-align: right;">
        <h3>Invoice Details</h3>
        <p><strong>Invoice #:</strong> {{invoice_number}}</p>
        <p><strong>Date:</strong> {{invoice_date}}</p>
        <p><strong>Due Date:</strong> {{due_date}}</p>
        <p><strong>Terms:</strong> {{payment_terms}}</p>
      </div>
    `,
    styles: { 
      textAlign: 'right', 
      color: '#333333', 
      fontSize: '14px',
      lineHeight: '1.6'
    }
  },
  keyValueList: {
    type: 'keyValueList',
    items: [
      ['Invoice Number', '{{invoice_number}}'],
      ['Invoice Date', '{{invoice_date}}'],
      ['Due Date', '{{due_date}}'],
      ['Payment Terms', '{{payment_terms}}'],
      ['PO Number', '{{po_number}}'],
      ['Currency', '{{currency}}'],
      ['Payment Method', '{{payment_method}}']
    ],
    styles: { 
      textAlign: 'right', 
      fontSize: '12px', 
      color: '#333333',
      borderCollapse: 'collapse'
    },
    validation: {
      minItems: 1,
      maxItems: 20
    }
  },
  billShip: {
    type: 'billShip',
    billTitle: 'Bill To',
    shipTitle: 'Ship To',
    billContent: `
      <div>
        <strong>{{bill_to_name}}</strong><br>
        {{bill_to_address}}<br>
        VAT: {{bill_to_vat}}<br>
        Customer #: {{customer_number}}
      </div>
    `,
    shipContent: `
      <div>
        <strong>{{ship_to_name}}</strong><br>
        {{ship_to_address}}<br>
        VAT: {{ship_to_vat}}<br>
        Customer #: {{customer_number}}
      </div>
    `,
    styles: { 
      marginTop: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      gap: '40px'
    }
  },
  lineItems: {
    type: 'lineItems',
    columns: [
      { id: uuidv4(), header: 'Description', key: 'description', width: '40%' },
      { id: uuidv4(), header: 'Qty', key: 'quantity', width: '10%', align: 'center' },
      { id: uuidv4(), header: 'Unit Price', key: 'unit_price', width: '15%', align: 'right' },
      { id: uuidv4(), header: 'Amount', key: 'billed_amount', width: '15%', align: 'right' },
      { id: uuidv4(), header: 'VAT', key: 'vat_amount', width: '10%', align: 'right' },
      { id: uuidv4(), header: 'Total', key: 'total', width: '10%', align: 'right' }
    ],
    data: [
      {
        id: uuidv4(),
        description: 'Professional Services<br><small>Period: {{period_from}} - {{period_to}}</small>',
        quantity: '1',
        unit_price: '{{unit_price}}',
        billed_amount: '{{billed_amount}}',
        vat_amount: '{{vat_amount}}',
        total: '{{line_total}}'
      }
    ],
    styles: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '20px'
    },
    validation: {
      minColumns: 2,
      maxColumns: 10,
      minRows: 1,
      maxRows: 50
    }
  },
  totals: {
    type: 'totals',
    rows: [
      ['Subtotal', '{{subtotal}}'],
      ['VAT', '{{vat_total}}'],
      ['Discount', '{{discount}}'],
      ['Total', '{{total_amount}}'],
      ['Amount Paid', '{{amount_paid}}'],
      ['Balance Due', '{{balance_due}}']
    ],
    styles: { 
      width: '40%', 
      marginLeft: 'auto', 
      marginTop: '20px',
      borderCollapse: 'collapse'
    },
    validation: {
      minRows: 1,
      maxRows: 20
    }
  },
  paymentInstructions: {
    type: 'paymentInstructions',
    content: `
      <div>
        <h4>Payment Instructions</h4>
        <p><strong>Bank Transfer:</strong></p>
        <p>{{bank_name}}<br>
        {{bank_street}}<br>
        {{bank_city}}</p>
        <p><strong>Account Details:</strong><br>
        Account #: {{bank_account}}<br>
        IBAN: {{bank_iban}}<br>
        Swift: {{bank_swift}}</p>
        <p><em>Please include invoice number in reference field.</em></p>
      </div>
    `,
    styles: { 
      marginTop: '30px',
      padding: '20px',
      backgroundColor: '#f8f9fa',
      borderRadius: '8px',
      border: '1px solid #e9ecef'
    }
  },
  textBlock: {
    type: 'textBlock',
    content: '<p>Thank you for your business. Please pay within the specified terms.</p>',
    styles: { 
      textAlign: 'left', 
      color: '#555555', 
      fontSize: '14px',
      lineHeight: '1.6',
      marginTop: '20px'
    }
  },
  divider: {
    type: 'divider',
    content: '',
    styles: {
      height: '2px',
      backgroundColor: '#e9ecef',
      margin: '20px 0',
      border: 'none'
    }
  },
  footerColumns: {
    type: 'footerColumns',
    content: [
      '<div><strong>Contact</strong><br>Phone: {{phone}}<br>Email: {{email}}</div>',
      '<div><strong>Address</strong><br>{{street}}<br>{{zip_city}}</div>',
      '<div><strong>Banking</strong><br>IBAN: {{iban}}<br>BIC: {{bic}}</div>',
      '<div><strong>Legal</strong><br>Terms & Conditions<br>Privacy Policy</div>'
    ],
    styles: { 
      display: 'flex', 
      justifyContent: 'space-between', 
      marginTop: '40px', 
      borderTop: '1px solid #eee', 
      paddingTop: '20px',
      fontSize: '12px',
      color: '#666'
    },
    validation: {
      minColumns: 2,
      maxColumns: 4
    }
  },
  termsConditions: {
    type: 'termsConditions',
    content: `
      <div>
        <h4>Terms & Conditions</h4>
        <p>Payment is due within {{payment_terms}} days of invoice date. Late payments may incur additional charges.</p>
        <p>All disputes are subject to the laws of {{country}}.</p>
      </div>
    `,
    styles: {
      marginTop: '30px',
      padding: '15px',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      fontSize: '12px',
      color: '#666',
      border: '1px solid #e9ecef'
    }
  },
  signature: {
    type: 'signature',
    content: `
      <div style="text-align: right;">
        <div style="border-top: 1px solid #000; width: 200px; margin-left: auto; margin-top: 40px;"></div>
        <p style="margin-top: 10px;">Authorized Signature</p>
      </div>
    `,
    styles: {
      marginTop: '40px'
    }
  },
  chart: {
    type: 'chart',
    chartType: 'bar',
    data: [
      { label: 'Q1', value: 1000 },
      { label: 'Q2', value: 1200 },
      { label: 'Q3', value: 800 },
      { label: 'Q4', value: 1500 }
    ],
    styles: {
      marginTop: '20px',
      height: '200px'
    }
  },
  qrCode: {
    type: 'qrCode',
    content: '{{invoice_number}}',
    size: 100,
    styles: {
      textAlign: 'right',
      marginTop: '20px'
    }
  },
  customHtml: {
    type: 'customHtml',
    content: '<div>Custom HTML content</div>',
    styles: {
      marginTop: '20px'
    }
  }
};

// Enhanced initial layout with better structure
const initialLayout = [
  { id: uuidv4(), ...componentLibrary.logo },
  { id: uuidv4(), ...componentLibrary.header },
  { id: uuidv4(), ...componentLibrary.companyInfo },
  { id: uuidv4(), ...componentLibrary.divider },
  { id: uuidv4(), ...componentLibrary.invoiceDetails },
  { id: uuidv4(), ...componentLibrary.billShip },
  { id: uuidv4(), ...componentLibrary.divider },
  { id: uuidv4(), ...componentLibrary.lineItems },
  { id: uuidv4(), type: 'customHtml', content: '<div style="margin-top:20px;">{{quarterly_summary}}</div>', styles: { marginTop: '20px' } },
  { id: uuidv4(), ...componentLibrary.totals },
  { id: uuidv4(), ...componentLibrary.paymentInstructions },
  { id: uuidv4(), ...componentLibrary.divider },
  { id: uuidv4(), ...componentLibrary.footerColumns },
];

const PdfTemplateEditor = () => {
  // Enhanced state management
  const [components, setComponents] = useState(initialLayout);
  const [selectedComponentId, setSelectedComponentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTemplateName, setCurrentTemplateName] = useState('recruiter_flexible_invoice_template');
  const [templateList, setTemplateList] = useState([]);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [showSampleData, setShowSampleData] = useState(false);
  const [showRenderDesign, setShowRenderDesign] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [autoSave, setAutoSave] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(20);

  // Refs for better performance
  const autoSaveTimeoutRef = useRef(null);
  const lastActionRef = useRef(null);

  // Enhanced sample data with more comprehensive fields
  const [sampleData, setSampleData] = useState({
    // Company Information
    company_name: 'Hello Doctor GmbH',
    company_address: 'Musterstrasse 123, 12345 Berlin, Germany',
    company_phone: '+49 30 12345678',
    company_email: 'info@hellodoctor.de',
    company_vat: 'DE123456789',
    company_website: 'www.hellodoctor.de',
    
    // Invoice Details
    invoice_number: 'INV-2025-001',
    invoice_date: '2025-01-15',
    due_date: '2025-02-14',
    payment_terms: 'NET 30',
    po_number: 'PO-2025-001',
    currency: 'EUR',
    payment_method: 'Bank Transfer',
    customer_id: 'CUST-001',
    
    // Billing Information
    bill_to_name: 'Sample Hospital GmbH',
    bill_to_address: 'Krankenhausstrasse 1, 10115 Berlin, Germany',
    bill_to_vat: 'DE987654321',
    ship_to_name: 'Sample Hospital GmbH',
    ship_to_address: 'Krankenhausstrasse 1, 10115 Berlin, Germany',
    ship_to_vat: 'DE987654321',
    customer_number: 'CUST-001',
    
    // Service Details
    order_number: 'ORD-2025-001',
    billing_frequency: 'Monthly',
    next_invoice_date: '2025-02-15',
    period_from: '2025-01-01',
    period_to: '2025-01-31',
    current_quarter: 'Q1',
    
    // Line Items
    unit_price: '€1,500.00',
    billed_amount: '€1,500.00',
    vat_amount: '€285.00',
    vat_percent: '19%',
    line_total: '€1,785.00',
    
    // Totals
    subtotal: '€1,500.00',
    vat_total: '€285.00',
    discount: '€0.00',
    total_amount: '€1,785.00',
    amount_paid: '€0.00',
    balance_due: '€1,785.00',
    
    // Banking Information
    bank_name: 'Deutsche Bank AG',
    bank_street: 'Unter den Linden 13',
    bank_city: '10117 Berlin, Germany',
    bank_account: '1234567890',
    bank_iban: 'DE89370400440532013000',
    bank_swift: 'COBADEFFXXX',
    remittance_email: 'payments@hellodoctor.de',
    
    // Contact Information
    phone: '+49 30 12345678',
    email: 'info@hellodoctor.de',
    street: 'Musterstrasse 123',
    zip_city: '12345 Berlin',
    iban: 'DE89370400440532013000',
    bic: 'COBADEFFXXX',
    
    // Additional Fields
    country: 'Germany',
    language: 'German',
    timezone: 'Europe/Berlin',
    logo_url: 'https://via.placeholder.com/200x80.png?text=Hello+Doctor',
    
    // Legacy fields for backward compatibility
    hospital_name: 'Sample Hospital GmbH',
    hospital_address: 'Krankenhausstrasse 1, 10115 Berlin, Germany',
    monthly_price: '€1,500.00',
    total_amount: '€1,785.00',
    net_amount: '€1,500.00',
    tax_amount: '€285.00',
    gross_amount: '€1,785.00',
    billing_period: 'January 2025',
    subscription_id: 'SUB-2025-001',
    start_date: '2025-01-01',
    end_date: '2025-12-31',
    payment_plan_type: 'Monthly',
    product_name: 'Professional Services',
    contract_duration: '12 months',
    quarterly_price: '€4,500.00',
    quarterly_vat: '€855.00',
    quarterly_total: '€5,355.00',
    annual_price_before_discount: '€18,000.00',
    annual_discount_value: '€1,800.00',
    annual_price_after_discount: '€16,200.00',
    annual_vat: '€3,078.00',
    annual_total: '€19,278.00',
    user_email: 'user@example.com',
    company_street: 'Musterstrasse 123',
    company_zip_city: '12345 Berlin',
  });

  // Enhanced available placeholders with categorization
  const availablePlaceholders = [
    // Company Information
    '{{company_name}}', '{{company_address}}', '{{company_phone}}', '{{company_email}}', '{{company_vat}}', '{{company_website}}',
    
    // Invoice Details
    '{{invoice_number}}', '{{invoice_date}}', '{{due_date}}', '{{payment_terms}}', '{{po_number}}', '{{currency}}', '{{payment_method}}', '{{customer_id}}',
    
    // Billing Information
    '{{bill_to_name}}', '{{bill_to_address}}', '{{bill_to_vat}}', '{{ship_to_name}}', '{{ship_to_address}}', '{{ship_to_vat}}', '{{customer_number}}',
    
    // Service Details
    '{{order_number}}', '{{billing_frequency}}', '{{next_invoice_date}}', '{{period_from}}', '{{period_to}}', '{{current_quarter}}',
    
    // Line Items
    '{{unit_price}}', '{{billed_amount}}', '{{vat_amount}}', '{{vat_percent}}', '{{line_total}}',
    
    // Totals
    '{{subtotal}}', '{{vat_total}}', '{{discount}}', '{{total_amount}}', '{{amount_paid}}', '{{balance_due}}',
    
    // Banking Information
    '{{bank_name}}', '{{bank_street}}', '{{bank_city}}', '{{bank_account}}', '{{bank_iban}}', '{{bank_swift}}', '{{remittance_email}}',
    
    // Contact Information
    '{{phone}}', '{{email}}', '{{street}}', '{{zip_city}}', '{{iban}}', '{{bic}}',
    
    // Additional Fields
    '{{country}}', '{{language}}', '{{timezone}}', '{{logo_url}}',
    
    // Legacy fields for backward compatibility
    '{{hospital_name}}', '{{hospital_address}}', '{{monthly_price}}', '{{total_amount}}', '{{net_amount}}', '{{tax_amount}}', '{{gross_amount}}', '{{billing_period}}', '{{subscription_id}}', '{{start_date}}', '{{end_date}}', '{{payment_plan_type}}', '{{product_name}}', '{{contract_duration}}', '{{quarterly_price}}', '{{quarterly_vat}}', '{{quarterly_total}}', '{{annual_price_before_discount}}', '{{annual_discount_value}}', '{{annual_price_after_discount}}', '{{annual_vat}}', '{{annual_total}}', '{{user_email}}', '{{company_street}}', '{{company_zip_city}}',
  ];

  // --- Shared placeholder + component rendering helpers (used by PDF + live preview) ---
  const toCss = (styles = {}) =>
    Object.entries(styles || {})
      .map(([k, v]) => `${String(k).replace(/([A-Z])/g, '-$1').toLowerCase()}:${v}`)
      .join(';');

  const applyPlaceholders = (html, data) => {
    if (!html) return '';
    let out = html;
    for (const key of Object.keys(data || {})) {
      const re = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g');
      out = out.replace(re, String(data[key] ?? ''));
    }
    return out;
  };

  // Enhanced validation system
  const validateComponent = (component) => {
    const errors = [];
    
    if (!component.type) {
      errors.push(`Component ${component.id} has no type`);
      return errors;
    }

    const validation = componentLibrary[component.type]?.validation;
    if (!validation) return errors;

    // Validate required fields
    if (validation.required && !component.content && !component.src) {
      errors.push(`${component.type} component requires content or source`);
    }

    // Validate line items
    if (component.type === 'lineItems') {
      if (component.columns && component.columns.length < validation.minColumns) {
        errors.push(`Line items must have at least ${validation.minColumns} columns`);
      }
      if (component.columns && component.columns.length > validation.maxColumns) {
        errors.push(`Line items cannot have more than ${validation.maxColumns} columns`);
      }
      if (component.data && component.data.length < validation.minRows) {
        errors.push(`Line items must have at least ${validation.minRows} rows`);
      }
      if (component.data && component.data.length > validation.maxRows) {
        errors.push(`Line items cannot have more than ${validation.maxRows} rows`);
      }
    }

    // Validate key-value lists
    if (component.type === 'keyValueList') {
      if (component.items && component.items.length < validation.minItems) {
        errors.push(`Key-value list must have at least ${validation.minItems} items`);
      }
      if (component.items && component.items.length > validation.maxItems) {
        errors.push(`Key-value list cannot have more than ${validation.maxItems} items`);
      }
    }

    // Validate footer columns
    if (component.type === 'footerColumns') {
      if (component.content && component.content.length < validation.minColumns) {
        errors.push(`Footer must have at least ${validation.minColumns} columns`);
      }
      if (component.content && component.content.length > validation.maxColumns) {
        errors.push(`Footer cannot have more than ${validation.maxColumns} columns`);
      }
    }

    return errors;
  };

  const validateTemplate = (comps) => {
    const errors = [];
    
    if (!Array.isArray(comps) || comps.length === 0) {
      errors.push('Template must have at least one component');
      return errors;
    }

    comps.forEach((component, index) => {
      const componentErrors = validateComponent(component);
      componentErrors.forEach(error => {
        errors.push(`Component ${index + 1}: ${error}`);
      });
    });

    return errors;
  };

  // Enhanced undo/redo system
  const saveToHistory = useCallback((newComponents, action = 'edit') => {
    const historyEntry = {
      components: JSON.parse(JSON.stringify(newComponents)),
      timestamp: Date.now(),
      action
    };

    setUndoStack(prev => [...prev, historyEntry]);
    setRedoStack([]); // Clear redo stack when new action is performed
    setIsDirty(true);
    lastActionRef.current = action;
  }, []);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;

    const currentState = {
      components: JSON.parse(JSON.stringify(components)),
      timestamp: Date.now(),
      action: 'current'
    };

    setRedoStack(prev => [currentState, ...prev]);
    
    const previousState = undoStack[undoStack.length - 1];
    setComponents(previousState.components);
    setUndoStack(prev => prev.slice(0, -1));
    setIsDirty(true);
  }, [undoStack, components]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;

    const currentState = {
      components: JSON.parse(JSON.stringify(components)),
      timestamp: Date.now(),
      action: 'current'
    };

    setUndoStack(prev => [...prev, currentState]);
    
    const nextState = redoStack[0];
    setComponents(nextState.components);
    setRedoStack(prev => prev.slice(1));
    setIsDirty(true);
  }, [redoStack, components]);

  // Auto-save functionality
  const autoSaveTemplate = useCallback(async () => {
    if (!autoSave || !currentTemplateName || !isDirty) return;

    const errors = validateTemplate(components);
    if (errors.length > 0) {
      console.warn('Auto-save skipped due to validation errors:', errors);
      return;
    }

    try {
      await axios.put(`/api2/pdf-templates/${currentTemplateName}`, {
        template_content: JSON.stringify(components),
        content: JSON.stringify(components)
      });
      setLastSaved(new Date());
      setIsDirty(false);
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [autoSave, currentTemplateName, isDirty, components]);

  // Enhanced component manipulation with history
  const updateComponents = useCallback((newComponents, action = 'edit') => {
    saveToHistory(newComponents, action);
    setComponents(newComponents);
  }, [saveToHistory]);

  const handleContentChange = (id, newContent) => {
    const newComponents = components.map(c => 
      c.id === id ? { ...c, content: newContent } : c
    );
    updateComponents(newComponents, 'content');
  };

  const handleLineItemDataChange = (componentId, rowIndex, key, value) => {
    const newComponents = components.map(comp => {
      if (comp.id === componentId && comp.type === 'lineItems') {
        const newData = [...comp.data];
        newData[rowIndex] = { ...newData[rowIndex], [key]: value };
        return { ...comp, data: newData };
      }
      return comp;
    });
    updateComponents(newComponents, 'lineItemData');
  };

  const handleFooterColumnContentChange = (id, columnIndex, newContent) => {
    const newComponents = components.map(c => {
      if (c.id === id && c.type === 'footerColumns') {
        const newContentArray = [...c.content];
        newContentArray[columnIndex] = newContent;
        return { ...c, content: newContentArray };
      }
      return c;
    });
    updateComponents(newComponents, 'footerColumnContent');
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    
    const newComponents = Array.from(components);
    const [reorderedItem] = newComponents.splice(result.source.index, 1);
    newComponents.splice(result.destination.index, 0, reorderedItem);
    
    updateComponents(newComponents, 'reorder');
  };

  const addComponent = (type) => {
    const newComponent = { id: uuidv4(), ...componentLibrary[type] };
    const newComponents = [...components, newComponent];
    updateComponents(newComponents, 'add');
  };

  const removeComponent = (id) => {
    const newComponents = components.filter(c => c.id !== id);
    updateComponents(newComponents, 'remove');
    
    if (selectedComponentId === id) {
      setSelectedComponentId(null);
    }
  };

  const duplicateComponent = (id) => {
    const componentToDuplicate = components.find(c => c.id === id);
    if (!componentToDuplicate) return;

    const duplicatedComponent = {
      ...componentToDuplicate,
      id: uuidv4()
    };

    const componentIndex = components.findIndex(c => c.id === id);
    const newComponents = [...components];
    newComponents.splice(componentIndex + 1, 0, duplicatedComponent);
    
    updateComponents(newComponents, 'duplicate');
    setSelectedComponentId(duplicatedComponent.id);
  };

  const handleSrcChange = (id, newSrc) => {
    const newComponents = components.map(c => 
      c.id === id ? { ...c, src: newSrc } : c
    );
    updateComponents(newComponents, 'source');
  };

  const handleStyleChange = (id, newStyle) => {
    const newComponents = components.map(c => {
      if (c.id === id) {
        return { ...c, styles: { ...c.styles, ...newStyle } };
      }
      return c;
    });
    updateComponents(newComponents, 'style');
  };

  // Enhanced line item management
  const handleLineItemColumnChange = (componentId, columnId, newHeader) => {
    const newComponents = components.map(comp => {
      if (comp.id === componentId && comp.type === 'lineItems') {
        return {
          ...comp,
          columns: comp.columns.map(col => 
            col.id === columnId ? { ...col, header: newHeader } : col
          )
        };
      }
      return comp;
    });
    updateComponents(newComponents, 'lineItemColumn');
  };

  const addLineItemColumn = (componentId) => {
    const newComponents = components.map(comp => {
      if (comp.id === componentId && comp.type === 'lineItems') {
        const newColumn = { 
          id: uuidv4(), 
          header: 'New Column', 
          key: `col_${comp.columns.length}`,
          width: '15%',
          align: 'left'
        };
        return {
          ...comp,
          columns: [...comp.columns, newColumn],
          data: comp.data.map(row => ({ ...row, [newColumn.key]: '' }))
        };
      }
      return comp;
    });
    updateComponents(newComponents, 'addLineItemColumn');
  };

  const removeLineItemColumn = (componentId, columnId) => {
    const newComponents = components.map(comp => {
      if (comp.id === componentId && comp.type === 'lineItems') {
        const updatedColumns = comp.columns.filter(col => col.id !== columnId);
        const removedColumnKey = comp.columns.find(col => col.id === columnId)?.key;
        const updatedData = comp.data.map(row => {
          const newRow = { ...row };
          delete newRow[removedColumnKey];
          return newRow;
        });
        return {
          ...comp,
          columns: updatedColumns,
          data: updatedData
        };
      }
      return comp;
    });
    updateComponents(newComponents, 'removeLineItemColumn');
  };

  const addLineItemRow = (componentId) => {
    const newComponents = components.map(comp => {
      if (comp.id === componentId && comp.type === 'lineItems') {
        const newRow = {
          id: uuidv4(),
          description: '',
          quantity: '1',
          unit_price: '',
          billed_amount: '',
          vat_amount: '',
          total: ''
        };
        return {
          ...comp,
          data: [...comp.data, newRow]
        };
      }
      return comp;
    });
    updateComponents(newComponents, 'addLineItemRow');
  };

  const removeLineItemRow = (componentId, rowIndex) => {
    const newComponents = components.map(comp => {
      if (comp.id === componentId && comp.type === 'lineItems') {
        const newData = comp.data.filter((_, index) => index !== rowIndex);
        return { ...comp, data: newData };
      }
      return comp;
    });
    updateComponents(newComponents, 'removeLineItemRow');
  };

  // Enhanced rendering with better error handling
  const renderComponentsToHtml = (comps) => {
    if (!Array.isArray(comps)) return '';
    
    return comps
      .map((comp) => {
        try {
          if (comp.type === 'logo') {
            const align = (comp.styles && comp.styles.textAlign) || 'left';
            const width = (comp.styles && comp.styles.width) || 'auto';
            const maxWidth = (comp.styles && comp.styles.maxWidth) || '100%';
            return `<div style="text-align:${align};"><img src="${comp.src}" alt="${comp.alt || 'Company Logo'}" style="width:${width}; max-width:${maxWidth}; height:auto;" /></div>`;
          }
          
          if (comp.type === 'lineItems') {
            const headerRow = (comp.columns || [])
              .map((col) => {
                const width = col.width || 'auto';
                const align = col.align || 'left';
                return `<th style="border:1px solid #ddd; padding:8px; text-align:${align}; width:${width}; background-color:#f2f2f2;">${col.header}</th>`;
              })
              .join('');
            
            const dataRows = (comp.data || [])
              .map((row) => {
                const cells = (comp.columns || [])
                  .map((col) => {
                    const align = col.align || 'left';
                    return `<td style="border:1px solid #ddd; padding:8px; text-align:${align};">${row[col.key] || ''}</td>`;
                  })
                  .join('');
                return `<tr>${cells}</tr>`;
              })
              .join('');
            
            return `<table style="width:100%; border-collapse:collapse; margin-top:20px;"><thead><tr>${headerRow}</tr></thead><tbody>${dataRows}</tbody></table>`;
          }
          
          if (comp.type === 'footerColumns') {
            const columnStyles = `width:25%; vertical-align:top; padding:0 10px; box-sizing:border-box; border:none;`;
            const columnsHtml = (comp.content || [])
              .map((colContent) => `<td style="${columnStyles}">${colContent}</td>`)
              .join('');
            return `<table class="footer-columns-table" style="width:100%; ${toCss(comp.styles || {})}"><tr>${columnsHtml}</tr></table>`;
          }
          
          if (comp.type === 'keyValueList') {
            const rows = (comp.items || [])
              .map(([label, value]) => `<tr><td style="padding:4px 8px; white-space:nowrap; color:#555;">${label}</td><td style="padding:4px 8px; text-align:right; font-weight:600;">${value}</td></tr>`)
              .join('');
            return `<table style="margin-left:auto; ${toCss(comp.styles || {})}"><tbody>${rows}</tbody></table>`;
          }
          
          if (comp.type === 'billShip') {
            const style = toCss(comp.styles || {});
            return `<table style="width:100%; ${style}"><tr>
              <td style="width:50%; vertical-align:top; padding-right:16px;"><h4>${comp.billTitle || 'Bill To'}</h4>${comp.billContent}</td>
              <td style="width:50%; vertical-align:top; padding-left:16px;"><h4>${comp.shipTitle || 'Ship To'}</h4>${comp.shipContent}</td>
            </tr></table>`;
          }
          
          if (comp.type === 'totals') {
            const rows = (comp.rows || [])
              .map(([label, value]) => `<tr><td style="padding:6px 8px; border:1px solid #ddd;">${label}</td><td style="padding:6px 8px; border:1px solid #ddd; text-align:right; font-weight:600;">${value}</td></tr>`)
              .join('');
            return `<table style="border-collapse:collapse; ${toCss(comp.styles || {})}"><tbody>${rows}</tbody></table>`;
          }
          
          if (comp.type === 'divider') {
            return `<hr style="${toCss(comp.styles || {})}" />`;
          }
          
          // Default / text-like blocks
          return `<div style="${toCss(comp.styles || {})}">${comp.content || ''}</div>`;
        } catch (error) {
          console.error(`Error rendering component ${comp.type}:`, error);
          return `<div style="color: red; border: 1px solid red; padding: 10px;">Error rendering ${comp.type} component</div>`;
        }
      })
      .join('');
  };

  // Enhanced CSS with better responsive design
  const PREVIEW_BASE_CSS = `
    body { 
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
      font-size: 12px; 
      color: #333; 
      line-height: 1.6;
      margin: 0;
      padding: 0;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-bottom: 20px;
    }
    th, td { 
      border: 1px solid #ddd; 
      padding: 8px; 
      vertical-align: top;
    }
    th { 
      background-color: #f2f2f2; 
      font-weight: 600;
    }
    .footer-columns-table td { 
      border: none; 
      vertical-align: top;
    }
    h1, h2, h3, h4, h5, h6 { 
      margin: 8px 0; 
      font-weight: 600;
    }
    h1 { font-size: 24px; }
    h2 { font-size: 20px; }
    h3 { font-size: 18px; }
    h4 { font-size: 16px; }
    p { margin: 8px 0; }
    img { 
      max-width: 100%; 
      height: auto; 
      display: block;
    }
    hr { 
      border: none; 
      border-top: 1px solid #e9ecef; 
      margin: 20px 0;
    }
    /* Paper frame for live preview */
    .pdf-paper { 
      width: 794px; 
      background: #fff; 
      padding: 40px; 
      box-sizing: border-box; 
      margin: 0 auto; 
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
      border-radius: 8px;
    }
    /* Responsive design */
    @media print {
      .pdf-paper { 
        width: 100%; 
        padding: 20px; 
        box-shadow: none; 
        border-radius: 0;
      }
    }
    @media (max-width: 800px) {
      .pdf-paper { 
        width: 100%; 
        padding: 20px; 
      }
    }
  `;

  // Apply base PDF CSS to the design area
  useEffect(() => {
    const styleId = 'pdf-template-editor-design-css';
    let styleTag = document.getElementById(styleId);
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      styleTag.type = 'text/css';
      styleTag.appendChild(document.createTextNode(PREVIEW_BASE_CSS));
      document.head.appendChild(styleTag);
    }
    return () => {
      const el = document.getElementById(styleId);
      if (el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  // Initialize on mount
  useEffect(() => {
    fetchTemplateList();
  }, []);

  // Enhanced effects and event handlers
  useEffect(() => {
    // Auto-save effect
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    if (autoSave && isDirty) {
      autoSaveTimeoutRef.current = setTimeout(() => {
        autoSaveTemplate();
      }, 3000); // Auto-save after 3 seconds of inactivity
    }

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [autoSave, isDirty, autoSaveTemplate]);

  // Define saveTemplate first using useCallback
  const saveTemplate = useCallback(async (name = currentTemplateName, comps = components) => {
    if (!name) {
      alert("Please select or create a template to save.");
      return;
    }
    
    if (comps.length === 0) {
      alert("Cannot save an empty template. Please add at least one component.");
      return;
    }

    // Validate template before saving
    const errors = validateTemplate(comps);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidationErrors(true);
      alert("Please fix validation errors before saving.");
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(`/api2/pdf-templates/${name}`, {
        template_content: JSON.stringify(comps),
        content: JSON.stringify(comps)
      });
      setLastSaved(new Date());
      setIsDirty(false);
      alert('Template saved successfully!');
    } catch (error) {
      if (error.response && error.response.status === 400 && /\s/.test(name)) {
        alert(`Failed to save template. The template name "${name}" contains spaces, which is not supported by the server. Please create a new template with a name that does not contain spaces.`);
      } else {
        console.error('Failed to save template:', error);
        alert('Error saving template. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentTemplateName, components]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              redo();
            } else {
              undo();
            }
            break;
          case 'y':
            e.preventDefault();
            redo();
            break;
          case 's':
            e.preventDefault();
            saveTemplate();
            break;
          case 'd':
            e.preventDefault();
            if (selectedComponentId) {
              duplicateComponent(selectedComponentId);
            }
            break;
          case 'Delete':
          case 'Backspace':
            e.preventDefault();
            if (selectedComponentId) {
              removeComponent(selectedComponentId);
            }
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, saveTemplate, selectedComponentId, duplicateComponent, removeComponent]);

  // Enhanced data fetching and saving with better error handling
  const fetchTemplateList = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api2/pdf-templates');
      const fetchedTemplates = Array.isArray(response.data) ? response.data : [];
      setTemplateList(fetchedTemplates);
      
      if (fetchedTemplates.length > 0) {
        setCurrentTemplateName(fetchedTemplates[0].template_name);
        await loadTemplate(fetchedTemplates[0].template_name);
      } else {
        setComponents(initialLayout);
      }
    } catch (error) {
      console.error("Failed to fetch template list:", error);
      setComponents(initialLayout);
      // Show user-friendly error message
      alert("Failed to load templates. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplate = async (name) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`/api2/pdf-templates/${name}`);
      if (response.data && (response.data.template_content || response.data.content)) {
        let content = response.data.template_content || response.data.content || "";
        let parsedContent = JSON.parse(content);

        // Migration for older templates
        parsedContent = parsedContent.map(component => {
          if (component.type === 'lineItems' && !component.columns) {
            return {
              ...component,
              ...componentLibrary.lineItems,
              id: component.id,
            };
          }
          return component;
        });

        // Validate loaded template
        const errors = validateTemplate(parsedContent);
        if (errors.length > 0) {
          console.warn('Template validation errors:', errors);
          setValidationErrors(errors);
          setShowValidationErrors(true);
        } else {
          setValidationErrors([]);
          setShowValidationErrors(false);
        }

        setComponents(parsedContent);
        setIsDirty(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log(`Template '${name}' not found, loading initial layout.`);
        setComponents(initialLayout);
      } else {
        console.error("Failed to load template:", error);
        alert("An error occurred while loading the template. Loading default template instead.");
        setComponents(initialLayout);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTemplateChange = (e) => {
    const name = e.target.value;
    setCurrentTemplateName(name);
    if (name) {
      loadTemplate(name);
    } else {
      setComponents(initialLayout);
    }
  };

  const handleCreateNewTemplate = async () => {
    if (!newTemplateName.trim()) {
      alert("Please enter a name for the new template.");
      return;
    }
    
    if (/\s/.test(newTemplateName)) {
      alert("Template names cannot contain spaces. Please choose a different name.");
      return;
    }
    
    if (templateList.some(t => t.template_name === newTemplateName)) {
      alert(`Template with name '${newTemplateName}' already exists. Please choose a different name or select the existing one.`);
      return;
    }
    
    if (components.length === 0) {
      alert("Cannot create a template from an empty canvas. Please add at least one component.");
      return;
    }

    // Validate template before creating
    const errors = validateTemplate(components);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidationErrors(true);
      alert("Please fix validation errors before creating a new template.");
      return;
    }

    setComponents(initialLayout);
    setCurrentTemplateName(newTemplateName);
    const newTemplate = {
      template_name: newTemplateName,
      template_content: JSON.stringify(initialLayout)
    };

    try {
      await axios.post("/api2/pdf-templates", newTemplate);
      alert("Template created successfully!");
      setNewTemplateName('');
      await fetchTemplateList();
    } catch (error) {
      console.error("Failed to create template:", error);
      alert("Error creating template. Please try again.");
    }
  };



  const previewPdf = async () => {
    const errors = validateTemplate(components);
    if (errors.length > 0) {
      setValidationErrors(errors);
      setShowValidationErrors(true);
      alert("Please fix validation errors before generating PDF.");
      return;
    }

    const finalHtml = generateFinalHtml();
    try {
      const response = await axios.post('/api2/pdf-preview', { htmlContent: finalHtml }, { responseType: 'blob' });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, '_blank');
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Failed to generate PDF preview:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleDeleteTemplate = async () => {
    if (!currentTemplateName || currentTemplateName === 'recruiter_flexible_invoice_template') {
      alert("Cannot delete the default template or no template selected.");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete template '${currentTemplateName}'? This action cannot be undone.`)) {
      return;
    }

    setIsLoading(true);
    try {
      await axios.delete(`/api2/pdf-templates/${currentTemplateName}`);
      alert('Template deleted successfully!');
      setCurrentTemplateName('');
      setComponents(initialLayout);
      await fetchTemplateList();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Error deleting template. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Render Design Widget - Shows template with dummy data
  const RenderDesignWidget = ({ templateComponents }) => {
    const renderDesignHtml = () => {
      let body = renderComponentsToHtml(templateComponents);
      // Always apply sample data for the render design widget
      body = applyPlaceholders(body, sampleData);
      return body;
    };

    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90vw',
        height: '90vh',
        background: 'rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(10px)',
        zIndex: 1000,
        borderRadius: '20px',
        padding: '20px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '16px 24px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          backdropFilter: 'blur(20px)'
        }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1a202c',
              margin: '0 0 4px 0',
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Render Design Preview
            </h2>
            <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
              Template: {currentTemplateName} | Showing with sample data
            </p>
          </div>
          <button
            onClick={() => setShowRenderDesign(false)}
            style={{
              width: '48px',
              height: '48px',
              background: '#f56565',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(245, 101, 101, 0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#e53e3e';
              e.target.style.transform = 'scale(1.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#f56565';
              e.target.style.transform = 'scale(1)';
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{
          height: 'calc(100% - 100px)',
          background: 'white',
          borderRadius: '16px',
          overflow: 'auto',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <div 
            className="pdf-paper"
            style={{
              maxWidth: '800px',
              margin: '0 auto',
              background: 'white',
              padding: '40px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}
            dangerouslySetInnerHTML={{ __html: renderDesignHtml() }}
          />
        </div>
      </div>
    );
  };

  // Enhanced HTML Generation
  const generateFinalHtml = () => {
    let body = renderComponentsToHtml(components);
    if (showSampleData) {
      body = applyPlaceholders(body, sampleData);
    }
    return `<!DOCTYPE html><html><head><meta charset="utf-8"/><style>${PREVIEW_BASE_CSS}</style></head><body>${body}</body></html>`;
  };

  const selectedComponent = components.find(c => c.id === selectedComponentId);

  return (
    <div 
    className='content'
    style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    }}>
      <div style={{ display: 'flex', height: '100vh' }}>
        
        {/* Left Sidebar - Enhanced Toolkit */}
        <div style={{
          width: '320px',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
          padding: '24px',
          overflowY: 'auto',
          boxShadow: '4px 0 20px rgba(0, 0, 0, 0.1)'
        }}>
          {/* Enhanced Header */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: '700', 
              color: '#1a202c',
              margin: '0 0 8px 0' 
            }}>
              Invoice Designer
            </h2>
            <p style={{ 
              color: '#718096', 
              fontSize: '14px', 
              margin: '0 0 12px 0' 
            }}>
              Create beautiful invoice templates
            </p>
            
            {/* Status Indicators */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isDirty && (
                <span style={{
                  padding: '4px 8px',
                  background: '#fbbf24',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  ⚠️ Unsaved
                </span>
              )}
              {lastSaved && (
                <span style={{
                  padding: '4px 8px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  ✓ Saved {lastSaved.toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>

          {/* Enhanced Template Management */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#2d3748', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              📁 Templates
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <select
                value={currentTemplateName}
                onChange={handleTemplateChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '14px',
                  background: 'white',
                  color: '#495057',
                  outline: 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
              >
                <option value="">-- New Template --</option>
                {templateList.map(template => (
                  <option key={template.template_id} value={template.template_name}>
                    {template.template_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="New template name"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '2px solid #e9ecef',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
              />
              <button
                onClick={handleCreateNewTemplate}
                disabled={isLoading || !newTemplateName}
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(45deg, #667eea, #764ba2)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isLoading || !newTemplateName ? 0.6 : 1
                }}
                onMouseOver={(e) => !e.target.disabled && (e.target.style.transform = 'translateY(-2px)')}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                ✨
              </button>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <button
                onClick={() => saveTemplate()}
                disabled={isLoading || /\s/.test(currentTemplateName)}
                title={/\s/.test(currentTemplateName) ? "Template names cannot contain spaces. Please use the 'Create' button to save this template with a new name." : "Save the current template"}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: '#48bb78',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isLoading || /\s/.test(currentTemplateName) ? 0.6 : 1
                }}
                onMouseOver={(e) => !e.target.disabled && (e.target.style.background = '#38a169')}
                onMouseOut={(e) => !e.target.disabled && (e.target.style.background = '#48bb78')}
              >
                {isLoading ? '⏳ Saving...' : '💾 Save'}
              </button>
              <button
                onClick={handleDeleteTemplate}
                disabled={isLoading || !currentTemplateName || currentTemplateName === 'recruiter_flexible_invoice_template'}
                style={{
                  padding: '12px 16px',
                  background: '#f56565',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isLoading || !currentTemplateName || currentTemplateName === 'recruiter_flexible_invoice_template' ? 0.3 : 1
                }}
                onMouseOver={(e) => !e.target.disabled && (e.target.style.background = '#e53e3e')}
                onMouseOut={(e) => !e.target.disabled && (e.target.style.background = '#f56565')}
              >
                🗑️
              </button>
            </div>

            {/* Enhanced Render Design Button */}
            <button
              onClick={() => setShowRenderDesign(true)}
              disabled={!currentTemplateName || components.length === 0}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(45deg, #9f7aea, #667eea)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: !currentTemplateName || components.length === 0 ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 12px rgba(159, 122, 234, 0.3)'
              }}
              onMouseOver={(e) => {
                if (!e.target.disabled) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 20px rgba(159, 122, 234, 0.4)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(159, 122, 234, 0.3)';
              }}
            >
              👁️ Render Design
            </button>
          </div>

          {/* Enhanced Component Library with Categories */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#2d3748', 
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🧩 Components
            </h3>
            
            {/* Header Components */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>Header</h4>
              <div style={{ display: 'grid', gap: '8px' }}>
                {['logo', 'header', 'companyInfo'].map(type => (
                  <button
                    key={type}
                    onClick={() => addComponent(type)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '2px solid transparent',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#2d3748',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#667eea';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.borderColor = 'transparent';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{componentIcons[type]}</span>
                    <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Components */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>Content</h4>
              <div style={{ display: 'grid', gap: '8px' }}>
                {['address', 'invoiceDetails', 'keyValueList', 'billShip', 'lineItems', 'totals', 'paymentInstructions', 'textBlock', 'divider'].map(type => (
                  <button
                    key={type}
                    onClick={() => addComponent(type)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '2px solid transparent',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#2d3748',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#667eea';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.borderColor = 'transparent';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{componentIcons[type]}</span>
                    <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer Components */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>Footer</h4>
              <div style={{ display: 'grid', gap: '8px' }}>
                {['footerColumns', 'termsConditions', 'signature'].map(type => (
                  <button
                    key={type}
                    onClick={() => addComponent(type)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '2px solid transparent',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#2d3748',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'left'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.borderColor = '#667eea';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                      e.target.style.borderColor = 'transparent';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>{componentIcons[type]}</span>
                    <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Enhanced Placeholders */}
          <div>
            <button
              onClick={() => setShowPlaceholders(!showPlaceholders)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid transparent',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#2d3748',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '12px'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'white';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                e.target.style.borderColor = 'transparent';
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                🏷️ Placeholders
              </span>
              <span style={{ transform: showPlaceholders ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                ▼
              </span>
            </button>
            
            {showPlaceholders && (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '6px',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                {availablePlaceholders.map(ph => (
                  <button
                    key={ph}
                    onClick={() => {
                      if (selectedComponentId && selectedComponent.type !== 'logo' && selectedComponent.type !== 'lineItems') {
                        handleContentChange(selectedComponentId, selectedComponent.content + ph);
                      }
                    }}
                    disabled={!selectedComponentId || selectedComponent?.type === 'logo' || selectedComponent?.type === 'lineItems'}
                    style={{
                      padding: '6px 12px',
                      background: selectedComponentId && selectedComponent?.type !== 'logo' && selectedComponent.type !== 'lineItems' ? '#667eea' : '#e2e8f0',
                      color: selectedComponentId && selectedComponent?.type !== 'logo' && selectedComponent.type !== 'lineItems' ? 'white' : '#a0aec0',
                      border: 'none',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: selectedComponentId && selectedComponent?.type !== 'logo' && selectedComponent.type !== 'lineItems' ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.background = '#5a67d8';
                        e.target.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!e.target.disabled) {
                        e.target.style.background = '#667eea';
                        e.target.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {ph.replace(/[{}]/g, '')}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Enhanced Sample Data Input */}
          <div style={{ marginTop: '32px' }}>
            <button
              onClick={() => setShowSampleData(!showSampleData)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid transparent',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#2d3748',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '12px'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'white';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                e.target.style.borderColor = 'transparent';
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                📊 Sample Data
              </span>
              <span style={{ transform: showSampleData ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                ▼
              </span>
            </button>
            {showSampleData && (
              <div style={{
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                {Object.keys(sampleData).map(key => (
                  <div key={key} style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: '#4a5568',
                      marginBottom: '6px'
                    }}>
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                    <input
                      type={key.includes('date') ? 'date' : 'text'}
                      value={sampleData[key]}
                      onChange={(e) => setSampleData({ ...sampleData, [key]: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '1px solid #cbd5e0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#cbd5e0'}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Advanced Settings */}
          <div style={{ marginTop: '32px' }}>
            <button
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.8)',
                border: '2px solid transparent',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                color: '#2d3748',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '12px'
              }}
              onMouseOver={(e) => {
                e.target.style.background = 'white';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.8)';
                e.target.style.borderColor = 'transparent';
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                ⚙️ Advanced Settings
              </span>
              <span style={{ transform: showAdvancedSettings ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}>
                ▼
              </span>
            </button>
            {showAdvancedSettings && (
              <div style={{
                padding: '16px',
                background: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}>
                {/* Auto-save toggle */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#4a5568',
                    cursor: 'pointer'
                  }}>
                                         <input
                       type="checkbox"
                       checked={autoSave}
                       onChange={(e) => setAutoSave(e.target.checked)}
                       style={{ width: '16px', height: '16px' }}
                     />
                     Auto-save
                   </label>
                 </div>

                 {/* Grid settings */}
                 <div style={{ marginBottom: '16px' }}>
                   <label style={{
                     display: 'flex',
                     alignItems: 'center',
                     gap: '8px',
                     fontSize: '14px',
                     fontWeight: '500',
                     color: '#4a5568',
                     cursor: 'pointer'
                   }}>
                     <input
                       type="checkbox"
                       checked={showGrid}
                       onChange={(e) => setShowGrid(e.target.checked)}
                       style={{ width: '16px', height: '16px' }}
                     />
                     Show Grid
                   </label>
                </div>

                {/* Zoom control */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Zoom: {zoomLevel}%
                  </label>
                  <input
                    type="range"
                    min="50"
                    max="200"
                    value={zoomLevel}
                    onChange={(e) => setZoomLevel(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: '#e2e8f0',
                      outline: 'none'
                    }}
                  />
                </div>

                {/* Keyboard shortcuts help */}
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748', marginBottom: '8px' }}>
                    ⌨️ Keyboard Shortcuts
                  </h4>
                  <div style={{ fontSize: '12px', color: '#4a5568', lineHeight: '1.4' }}>
                    <div>Ctrl+Z: Undo</div>
                    <div>Ctrl+Shift+Z: Redo</div>
                    <div>Ctrl+S: Save</div>
                    <div>Ctrl+D: Duplicate</div>
                    <div>Delete: Remove</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Canvas Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Top Bar */}
          <div style={{
            padding: '24px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <div>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: '700', 
                color: '#1a202c', 
                margin: 0,
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Invoice Designer
              </h1>
              <p style={{ color: '#718096', fontSize: '14px', margin: '4px 0 0 0' }}>
                {currentTemplateName || 'Untitled Template'}
              </p>
            </div>
            <button
              onClick={previewPdf}
              style={{
                padding: '16px 32px',
                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-3px)';
                e.target.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
              }}
            >
              👁️ Preview PDF
            </button>
          </div>

          {/* Canvas */}
          <div style={{ 
            flex: 1, 
            padding: '24px', 
            overflow: 'auto',
            background: 'rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{
              maxWidth: '800px',
              margin: '0 auto',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="invoice-canvas">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      style={{
                        minHeight: '800px',
                        padding: '40px',
                        background: snapshot.isDraggingOver ? 'rgba(102, 126, 234, 0.05)' : 'white',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      {components.map((component, index) => (
                        <Draggable key={component.id} draggableId={component.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onClick={() => setSelectedComponentId(component.id)}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: '16px',
                                padding: '20px',
                                border: selectedComponentId === component.id 
                                  ? '3px solid #667eea' 
                                  : '2px solid transparent',
                                borderRadius: '16px',
                                background: snapshot.isDragging 
                                  ? 'rgba(102, 126, 234, 0.1)' 
                                  : selectedComponentId === component.id 
                                    ? 'rgba(102, 126, 234, 0.05)' 
                                    : 'rgba(248, 250, 252, 0.8)',
                                cursor: 'grab',
                                transition: 'all 0.2s ease',
                                position: 'relative',
                                boxShadow: snapshot.isDragging 
                                  ? '0 20px 40px rgba(0, 0, 0, 0.15)' 
                                  : selectedComponentId === component.id 
                                    ? '0 8px 25px rgba(102, 126, 234, 0.15)' 
                                    : '0 2px 8px rgba(0, 0, 0, 0.05)',
                                transform: snapshot.isDragging 
                                  ? `${provided.draggableProps.style.transform} rotate(2deg)` 
                                  : provided.draggableProps.style.transform
                              }}
                              onMouseOver={(e) => {
                                if (selectedComponentId !== component.id && !snapshot.isDragging) {
                                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.08)';
                                  e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.3)';
                                }
                              }}
                              onMouseOut={(e) => {
                                if (selectedComponentId !== component.id && !snapshot.isDragging) {
                                  e.currentTarget.style.background = 'rgba(248, 250, 252, 0.8)';
                                  e.currentTarget.style.borderColor = 'transparent';
                                }
                              }}
                            >
                              {/* Component Type Badge */}
                              <div style={{
                                position: 'absolute',
                                top: '-12px',
                                left: '16px',
                                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                color: 'white',
                                padding: '4px 12px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '600',
                                display: selectedComponentId === component.id ? 'flex' : 'none',
                                alignItems: 'center',
                                gap: '4px',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                              }}>
                                <span>{componentIcons[component.type]}</span>
                                {component.type}
                              </div>

                              {/* Delete Button */}
                              {selectedComponentId === component.id && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeComponent(component.id);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    width: '32px',
                                    height: '32px',
                                    background: '#f56565',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 4px 12px rgba(245, 101, 101, 0.3)'
                                  }}
                                  onMouseOver={(e) => {
                                    e.target.style.background = '#e53e3e';
                                    e.target.style.transform = 'scale(1.1)';
                                  }}
                                  onMouseOut={(e) => {
                                    e.target.style.background = '#f56565';
                                    e.target.style.transform = 'scale(1)';
                                  }}
                                >
                                  ✕
                                </button>
                              )}

                              {/* Component Content */}
                              {(() => {
                                if (component.type === 'logo') {
                                  return (
                                    <div style={{ textAlign: component.styles.textAlign }}>
                                      <img 
                                        src={component.src} 
                                        alt="Company Logo" 
                                        style={{ 
                                          width: component.styles.width,
                                          borderRadius: '8px',
                                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                                        }} 
                                      />
                                    </div>
                                  );
                                } else if (component.type === 'lineItems') {
                                  return (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr>
                                          {component.columns.map(col => (
                                            <th key={col.id} style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', background: '#f2f2f2' }}>
                                              <ContentEditable
                                                html={col.header}
                                                onChange={(e) => handleLineItemColumnChange(component.id, col.id, e.target.value)}
                                                tagName="div"
                                                style={{ outline: 'none', minHeight: '20px' }}
                                              />
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {component.data.map((row, rowIndex) => (
                                          <tr key={row.id}>
                                            {component.columns.map(col => (
                                              <td key={col.id} style={{ border: '1px solid #ddd', padding: '8px' }}>
                                                <ContentEditable
                                                  html={row[col.key] || ''}
                                                  onChange={(e) => handleLineItemDataChange(component.id, rowIndex, col.key, e.target.value)}
                                                  tagName="div"
                                                  style={{ outline: 'none', minHeight: '20px' }}
                                                />
                                              </td>
                                            ))}
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  );
                                } else if (component.type === 'footerColumns') {
                                  return (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', ...component.styles }}>
                                      {component.content.map((colContent, colIndex) => (
                                        <div key={colIndex} style={{ flex: 1, padding: '0 10px', border: 'none' }}>
                                          <ContentEditable
                                            html={colContent}
                                            onChange={(e) => handleFooterColumnContentChange(component.id, colIndex, e.target.value)}
                                            tagName="div"
                                            style={{ outline: 'none', minHeight: '20px' }}
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  );
                                } else {
                                  return (
                                    <ContentEditable
                                      html={component.content}
                                      onChange={(e) => handleContentChange(component.id, e.target.value)}
                                      tagName="div"
                                      style={{
                                        ...component.styles,
                                        outline: 'none',
                                        minHeight: '20px'
                                      }}
                                    />
                                  );
                                }
                              })()}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {/* Empty State */}
                      {components.length === 0 && (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '400px',
                          color: '#a0aec0',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📄</div>
                          <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
                            No Components Added
                          </h3>
                          <p style={{ fontSize: '14px', margin: 0 }}>
                            Start building your invoice by adding components from the sidebar
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Properties Panel */}
        {selectedComponent && (
          <div style={{
            width: '320px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '24px',
            overflowY: 'auto',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Header */}
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#1a202c',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{componentIcons[selectedComponent.type]}</span>
                {selectedComponent.type.charAt(0).toUpperCase() + selectedComponent.type.slice(1)} Settings
              </h3>
              <p style={{ 
                color: '#718096', 
                fontSize: '14px', 
                margin: 0 
              }}>
                Customize the appearance and behavior
              </p>
            </div>

            {/* Settings Form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {selectedComponent.type === 'logo' ? (
                <>
                  {/* Logo URL */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      🔗 Logo URL
                    </label>
                    <input
                      type="text"
                      value={selectedComponent.src}
                      onChange={(e) => handleSrcChange(selectedComponentId, e.target.value)}
                      placeholder="https://example.com/logo.png"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        fontFamily: 'monospace'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  {/* Logo Width */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      📏 Width
                    </label>
                    <input
                      type="text"
                      value={selectedComponent.styles.width}
                      onChange={(e) => handleStyleChange(selectedComponentId, { width: e.target.value })}
                      placeholder="250px or 50%"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>

                  {/* Alignment */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      📐 Alignment
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {['left', 'center', 'right'].map(align => (
                        <button
                          key={align}
                          onClick={() => handleStyleChange(selectedComponentId, { textAlign: align })}
                          style={{
                            flex: 1,
                            padding: '12px',
                            background: selectedComponent.styles.textAlign === align ? '#667eea' : 'white',
                            color: selectedComponent.styles.textAlign === align ? 'white' : '#2d3748',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textTransform: 'capitalize'
                          }}
                          onMouseOver={(e) => {
                            if (selectedComponent.styles.textAlign !== align) {
                              e.target.style.borderColor = '#667eea';
                              e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (selectedComponent.styles.textAlign !== align) {
                              e.target.style.borderColor = '#e9ecef';
                              e.target.style.background = 'white';
                            }
                          }}
                        >
                          {align}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : selectedComponent.type === 'lineItems' ? (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '8px'
                  }}>
                    📊 Table Columns
                  </label>
                  {selectedComponent.columns.map((col) => (
                    <div key={col.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                      <input
                        type="text"
                        value={col.header}
                        onChange={(e) => handleLineItemColumnChange(selectedComponentId, col.id, e.target.value)}
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          fontSize: '14px',
                          outline: 'none',
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                      <button
                        onClick={() => removeLineItemColumn(selectedComponentId, col.id)}
                        style={{
                          padding: '12px',
                          background: '#f56565',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => addLineItemColumn(selectedComponentId)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#48bb78',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    Add Column
                  </button>
                </div>
              ) : selectedComponent.type === 'footerColumns' ? (
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '8px'
                  }}>
                    📝 Column Content
                  </label>
                  {selectedComponent.content.map((colContent, colIndex) => (
                    <div key={colIndex} style={{ marginBottom: '8px' }}>
                      <ContentEditable
                        html={colContent}
                        onChange={(e) => handleFooterColumnContentChange(selectedComponentId, colIndex, e.target.value)}
                        tagName="div"
                        style={{
                          outline: 'none',
                          minHeight: '20px',
                          padding: '12px 16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          fontSize: '14px',
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Content */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      📝 Content
                    </label>
                    <ContentEditable
                      html={selectedComponent.content}
                      onChange={(e) => handleContentChange(selectedComponentId, e.target.value)}
                      tagName="div"
                      style={{
                        ...selectedComponent.styles,
                        outline: 'none',
                        minHeight: '20px'
                      }}
                    />
                  </div>

                  {/* Font Family */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      🔤 Font Family
                    </label>
                    <select
                      value={selectedComponent.styles.fontFamily || 'Arial, sans-serif'}
                      onChange={(e) => handleStyleChange(selectedComponentId, { fontFamily: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '14px',
                        background: 'white',
                        color: '#495057',
                        outline: 'none',
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    >
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="'Helvetica Neue', Helvetica, Arial, sans-serif">Helvetica</option>
                      <option value="'Times New Roman', Times, serif">Times New Roman</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                      <option value="Georgia, serif">Georgia</option>
                    </select>
                  </div>

                  {/* Font Size & Weight Row */}
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '8px'
                      }}>
                        📏 Size
                      </label>
                      <input
                        type="text"
                        value={selectedComponent.styles.fontSize || ''}
                        onChange={(e) => handleStyleChange(selectedComponentId, { fontSize: e.target.value })}
                        placeholder="16px"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '8px'
                      }}>
                        💪 Weight
                      </label>
                      <input
                        type="text"
                        value={selectedComponent.styles.fontWeight || ''}
                        onChange={(e) => handleStyleChange(selectedComponentId, { fontWeight: e.target.value })}
                        placeholder="bold"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'all 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </div>
                  </div>

                  {/* Color */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      🎨 Text Color
                    </label>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={selectedComponent.styles.color || '#000000'}
                        onChange={(e) => handleStyleChange(selectedComponentId, { color: e.target.value })}
                        style={{
                          width: '60px',
                          height: '48px',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          background: 'none'
                        }}
                      />
                      <input
                        type="text"
                        value={selectedComponent.styles.color || '#000000'}
                        onChange={(e) => handleStyleChange(selectedComponentId, { color: e.target.value })}
                        placeholder="#000000"
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          border: '2px solid #e9ecef',
                          borderRadius: '12px',
                          fontSize: '14px',
                          outline: 'none',
                          transition: 'all 0.2s ease',
                          fontFamily: 'monospace'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                      />
                    </div>
                  </div>

                  {/* Text Alignment */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      📐 Text Alignment
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { value: 'left', icon: '⬅️' },
                        { value: 'center', icon: '↔️' },
                        { value: 'right', icon: '➡️' }
                      ].map(({ value, icon }) => (
                        <button
                          key={value}
                          onClick={() => handleStyleChange(selectedComponentId, { textAlign: value })}
                          style={{
                            flex: 1,
                            padding: '12px',
                            background: selectedComponent.styles.textAlign === value ? '#667eea' : 'white',
                            color: selectedComponent.styles.textAlign === value ? 'white' : '#2d3748',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px'
                          }}
                          onMouseOver={(e) => {
                            if (selectedComponent.styles.textAlign !== value) {
                              e.target.style.borderColor = '#667eea';
                              e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (selectedComponent.styles.textAlign !== value) {
                              e.target.style.borderColor = '#e9ecef';
                              e.target.style.background = 'white';
                            }
                          }}
                        >
                          <span>{icon}</span>
                          <span style={{ textTransform: 'capitalize' }}>{value}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Padding */}
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#2d3748',
                      marginBottom: '8px'
                    }}>
                      📦 Padding
                    </label>
                    <input
                      type="text"
                      value={selectedComponent.styles.padding || ''}
                      onChange={(e) => handleStyleChange(selectedComponentId, { padding: e.target.value })}
                      placeholder="10px or 5px 10px"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: '2px solid #e9ecef',
                        borderRadius: '12px',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* No Selection State */}
        {!selectedComponent && (
          <div style={{
            width: '320px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderLeft: '1px solid rgba(255, 255, 255, 0.2)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            color: '#a0aec0',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚙️</div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
              No Component Selected
            </h3>
            <p style={{ fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
              Click on a component in the canvas to customize its appearance and properties
            </p>
          </div>
        )}
      </div>

      {/* Render Design Widget Overlay */}
      {showRenderDesign && (
        <RenderDesignWidget templateComponents={components} />
      )}
    </div>
  );
};

export default PdfTemplateEditor;